import { useRequiredContext } from "@redotech/react-util/context";
import {
  type MerchantRoutes,
  MerchantRoutesContext,
} from "@redotech/redo-merchant-app-common/merchant-routes";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Flex } from "@redotech/redo-web/flex";
import { SmartDateDisplay } from "@redotech/redo-web/smart-date-display";
import { Text } from "@redotech/redo-web/text";
import { DATE_FORMAT } from "@redotech/redo-web/time";
import {
  type FaqKnowledgeId,
  type MerchantKnowledgeId,
  type SuggestedMerchantKnowledgeId,
} from "@redotech/shopper-agent-knowledge-id";
import { memo, type ReactNode, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AskRedoDisclosureButton } from "../ask-redo-disclosure-button";
import * as styles from "./concierge-cards.module.css";
import {
  ConciergeAutomationDetailOutput,
  ConciergeAutomationListOutput,
  ConciergeConfigOutput,
  ConciergeConfigUpdateOutput,
  ConciergeConversationDetailOutput,
  ConciergeConversationListOutput,
  ConciergeConversationSummary,
  ConciergeCustomerOutput,
  ConciergeKnowledge,
  ConciergeKnowledgeSearchOutput,
  ConciergeMessage,
  conversationState,
  customerDisplayName,
  customerId,
  primaryCustomerEmail,
  primaryCustomerPhone,
} from "./parse-concierge";

const LIST_PREVIEW_COUNT = 3;
const MAX_LIST_PREVIEW_COUNT = 9;
const MESSAGE_PREVIEW_COUNT = 10;
const AUTOMATION_PREVIEW_COUNT = 3;
const MAX_AUTOMATION_PREVIEW_COUNT = 9;
const KNOWLEDGE_PREVIEW_COUNT = 3;

export const ConciergeConfigCard = memo(function ConciergeConfigCard({
  data,
}: {
  data: ConciergeConfigOutput;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const activeTriggers =
    data.triggerCatalog?.triggers?.filter(
      (trigger) => trigger.effective?.willConsiderSignal,
    ) ?? [];
  const channelDefinitions = data.channelDefinitions ?? [];
  const hasStatusSection =
    data.renderView.includes("status") && Boolean(data.settings);
  const hasChannelsSection =
    data.renderView.includes("channels") && channelDefinitions.length > 0;
  const hasTriggerSection =
    data.renderView.includes("trigger") && Boolean(data.trigger);
  const hasTriggerSummary =
    data.renderView.includes("triggerCatalog") &&
    Boolean(data.triggerCatalog) &&
    activeTriggers.length > 0;
  const hasConfigRowsAfterStatus =
    hasChannelsSection || hasTriggerSection || hasTriggerSummary;
  const hasConfigRowsAfterChannels = hasTriggerSection || hasTriggerSummary;
  const hasConfigRowsAfterTrigger = hasTriggerSummary;
  const activeTriggerNames = activeTriggers.map(triggerName);
  const activeTriggerSummary =
    activeTriggerNames.length <= 3
      ? activeTriggerNames.join(", ")
      : `${activeTriggerNames.slice(0, 3).join(", ")} and ${
          activeTriggerNames.length - 3
        } more`;

  return (
    <MiniDetailSurface
      actionHref={routes.getShopperAgentSubRoute("settings")}
      actionLabel="View Commerce Agent settings"
      title="Commerce Agent config"
    >
      {hasStatusSection && data.settings && (
        <ConfigSection title="Status">
          <ConfigSnapshotRow
            badge={
              <StatusBadge
                enabled={data.settings.enabled}
                enabledText="On"
                offText="Off"
              />
            }
            borderBottom={Boolean(
              data.settings.replyConfig ||
              data.settings.extendedOutreachConfig ||
              hasConfigRowsAfterStatus,
            )}
            label="Commerce Agent"
            value={
              data.settings.personaConfig?.name
                ? `${data.settings.personaConfig.name} is active`
                : data.settings.enabled
                  ? "Active"
                  : "Disabled"
            }
          />
          {data.settings.replyConfig && (
            <ConfigSnapshotRow
              badge={
                <StatusBadge
                  enabled={data.settings.replyConfig.enabled}
                  enabledText="On"
                  offText="Off"
                />
              }
              borderBottom={Boolean(
                data.settings.extendedOutreachConfig ||
                hasConfigRowsAfterStatus,
              )}
              label="Replies"
              value={
                data.settings.replyConfig.enabled
                  ? "Commerce Agent can reply to shoppers"
                  : "Reply automation is off"
              }
            />
          )}
          {data.settings.extendedOutreachConfig && (
            <ConfigSnapshotRow
              badge={
                <StatusBadge
                  enabled={data.settings.extendedOutreachConfig.enabled}
                  enabledText="On"
                  offText="Off"
                />
              }
              borderBottom={hasConfigRowsAfterStatus}
              label="Extended outreach"
              value={
                data.settings.extendedOutreachConfig.enabled
                  ? "Follow-up outreach is enabled"
                  : "Follow-up outreach is off"
              }
            />
          )}
        </ConfigSection>
      )}

      {hasChannelsSection && (
        <ConfigSection title="Channels">
          {channelDefinitions.map((definition, index) => (
            <ConfigSnapshotRow
              badge={
                <StatusBadge
                  enabled={isChannelEnabled(data, definition.channel)}
                  enabledText="On"
                  offText="Off"
                />
              }
              borderBottom={
                index < channelDefinitions.length - 1 ||
                hasConfigRowsAfterChannels
              }
              key={definition.channel}
              label={definition.name}
              value={
                isChannelEnabled(data, definition.channel)
                  ? "Available for Commerce Agent"
                  : "Not currently available"
              }
            />
          ))}
        </ConfigSection>
      )}

      {hasTriggerSection && data.trigger && (
        <ConfigSection title="Trigger">
          <ConfigSnapshotRow
            badge={
              <StatusBadge
                enabled={
                  data.trigger.supported &&
                  data.trigger.effective?.willConsiderSignal
                }
                enabledText="Active"
                offText="Inactive"
              />
            }
            borderBottom={hasConfigRowsAfterTrigger}
            label={triggerName(data.trigger)}
            value={triggerSubtitle(data.trigger) || "Ready for Commerce Agent"}
          />
        </ConfigSection>
      )}

      {data.renderView.includes("triggerCatalog") && data.triggerCatalog && (
        <ConfigSection title="Triggers">
          {activeTriggers.length > 0 ? (
            <ConfigSnapshotRow
              badge={
                <RedoBadge
                  color="success"
                  size="xs"
                  text={`${activeTriggers.length} active`}
                />
              }
              borderBottom={false}
              label="Currently active"
              value={activeTriggerSummary}
            />
          ) : (
            <Text fontSize="xs" textColor="secondary">
              No active triggers are currently configured.
            </Text>
          )}
        </ConfigSection>
      )}
    </MiniDetailSurface>
  );
});

export const ConciergeConfigUpdateCard = memo(
  function ConciergeConfigUpdateCard({
    data,
  }: {
    data: ConciergeConfigUpdateOutput;
  }) {
    const routes = useRequiredContext(MerchantRoutesContext);

    return (
      <MiniDetailSurface
        actionHref={routes.getShopperAgentSubRoute("settings")}
        actionLabel="View Commerce Agent settings"
        title="Commerce Agent settings updated"
      >
        <FullBleedList>
          {data.changes.map((change, index) => (
            <ConfigChangeRow
              change={change}
              hasDivider={index < data.changes.length - 1}
              key={`${change.label}-${index}`}
            />
          ))}
        </FullBleedList>
      </MiniDetailSurface>
    );
  },
);

export const ConciergeAutomationListCard = memo(
  function ConciergeAutomationListCard({
    data,
  }: {
    data: ConciergeAutomationListOutput;
  }) {
    const routes = useRequiredContext(MerchantRoutesContext);
    const [visibleAutomationCount, setVisibleAutomationCount] = useState(
      AUTOMATION_PREVIEW_COUNT,
    );
    const visibleAutomations = data.automations.slice(
      0,
      visibleAutomationCount,
    );
    const hiddenCount = Math.max(
      0,
      data.automations.length - visibleAutomationCount,
    );
    const canShowMore =
      hiddenCount > 0 && visibleAutomationCount < MAX_AUTOMATION_PREVIEW_COUNT;
    const hasHiddenReturnedResults =
      hiddenCount > 0 && visibleAutomationCount >= MAX_AUTOMATION_PREVIEW_COUNT;

    return (
      <MiniDetailSurface title="Commerce Agent automations">
        {data.automations.length === 0 ? (
          <Text fontSize="xs" textColor="secondary">
            No matching Commerce Agent automations found.
          </Text>
        ) : (
          <FullBleedList>
            {visibleAutomations.map((automation) => (
              <AutomationSummaryRow
                automationHref={getAutomationHref(
                  routes,
                  automation.automation.id,
                )}
                data={automation}
                isLast={
                  automation.automation.id ===
                  visibleAutomations[visibleAutomations.length - 1]?.automation
                    .id
                }
                key={automation.automation.id}
              />
            ))}
          </FullBleedList>
        )}

        {canShowMore && (
          <AskRedoDisclosureButton
            isOpen={visibleAutomationCount > AUTOMATION_PREVIEW_COUNT}
            onToggle={() =>
              setVisibleAutomationCount((count) =>
                Math.min(
                  data.automations.length,
                  count + AUTOMATION_PREVIEW_COUNT,
                  MAX_AUTOMATION_PREVIEW_COUNT,
                ),
              )
            }
          >
            <Text fontSize="xs" fontWeight="semibold" textColor="brand-primary">
              Show {Math.min(AUTOMATION_PREVIEW_COUNT, hiddenCount)} more
            </Text>
          </AskRedoDisclosureButton>
        )}

        {hasHiddenReturnedResults && (
          <Text fontSize="xs" textColor="secondary">
            More returned automations are hidden. Narrow the search to inspect
            the rest.
          </Text>
        )}
      </MiniDetailSurface>
    );
  },
);

export const ConciergeAutomationCard = memo(function ConciergeAutomationCard({
  data,
}: {
  data: ConciergeAutomationDetailOutput;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const { automation, concierge } = data;

  return (
    <MiniDetailSurface
      actionHref={getAutomationHref(routes, automation.id)}
      actionLabel="View automation"
      badge={<AutomationEnabledBadge enabled={automation.enabled} />}
      subtitle="Automation"
      title={automation.name}
    >
      <Flex dir="column" gap="md">
        {automation.description && (
          <Text fontSize="xs" textColor="secondary">
            {automation.description}
          </Text>
        )}
        <FullBleedList>
          <ConfigSnapshotRow
            borderBottom
            label="Trigger"
            value={concierge.triggerName}
          />
          <ConfigSnapshotRow
            borderBottom={false}
            label="Steps"
            value={stepCountLabel(automation.stepCount, automation.steps)}
          />
        </FullBleedList>
      </Flex>
    </MiniDetailSurface>
  );
});

const MiniListRow = memo(function MiniListRow({
  children,
  hasDivider,
  onClick,
  py = "lg",
}: {
  children: ReactNode;
  hasDivider: boolean;
  onClick?: () => void;
  py?: "sm" | "md" | "lg";
}) {
  if (onClick) {
    return (
      <Flex
        as="button"
        borderBottomWidth={hasDivider ? "1px" : undefined}
        borderColor="secondary"
        borderStyle="solid"
        className={styles.conversationListItem}
        dir="column"
        gap="sm"
        onClick={onClick}
        px="lg"
        py={py}
        w="full"
      >
        {children}
      </Flex>
    );
  }

  return (
    <Flex
      align="center"
      borderBottomWidth={hasDivider ? "1px" : undefined}
      borderColor="secondary"
      borderStyle="solid"
      gap="md"
      justify="space-between"
      px="lg"
      py={py}
    >
      {children}
    </Flex>
  );
});

export const ConciergeConversationListCard = memo(
  function ConciergeConversationListCard({
    data,
  }: {
    data: ConciergeConversationListOutput;
  }) {
    const routes = useRequiredContext(MerchantRoutesContext);
    const [visibleConversationCount, setVisibleConversationCount] =
      useState(LIST_PREVIEW_COUNT);
    const conversations = data.conversations;
    const visibleConversations = conversations.slice(
      0,
      visibleConversationCount,
    );
    const hiddenCount = Math.max(
      0,
      conversations.length - visibleConversationCount,
    );
    const canShowMore =
      hiddenCount > 0 && visibleConversationCount < MAX_LIST_PREVIEW_COUNT;
    const hasHiddenReturnedResults =
      hiddenCount > 0 && visibleConversationCount >= MAX_LIST_PREVIEW_COUNT;

    return (
      <MiniDetailSurface
        actionHref={routes.getShopperAgentSubRoute("conversations")}
        actionLabel="Open conversations"
        subtitle="Commerce Agent"
        title="Conversations"
      >
        {conversations.length === 0 ? (
          <Text fontSize="xs" textColor="secondary">
            No matching Commerce Agent support conversations found.
          </Text>
        ) : (
          <FullBleedList>
            {visibleConversations.map((conversation) => (
              <ConversationSummaryRow
                conversation={conversation}
                conversationHref={getConversationHref(
                  routes,
                  conversation.topicId,
                )}
                isLast={
                  conversation.topicId ===
                  visibleConversations[visibleConversations.length - 1]?.topicId
                }
                key={conversation.topicId}
              />
            ))}
          </FullBleedList>
        )}

        {canShowMore && (
          <AskRedoDisclosureButton
            isOpen={visibleConversationCount > LIST_PREVIEW_COUNT}
            onToggle={() =>
              setVisibleConversationCount((count) =>
                Math.min(
                  conversations.length,
                  count + LIST_PREVIEW_COUNT,
                  MAX_LIST_PREVIEW_COUNT,
                ),
              )
            }
          >
            <Text fontSize="xs" fontWeight="semibold" textColor="brand-primary">
              Show {Math.min(LIST_PREVIEW_COUNT, hiddenCount)} more
            </Text>
          </AskRedoDisclosureButton>
        )}

        {hasHiddenReturnedResults && (
          <Text fontSize="xs" textColor="secondary">
            More returned conversations are hidden. Open conversations or narrow
            the search to inspect the rest.
          </Text>
        )}
      </MiniDetailSurface>
    );
  },
);

export const ConciergeConversationCard = memo(
  function ConciergeConversationCard({
    data,
  }: {
    data: ConciergeConversationDetailOutput;
  }) {
    const routes = useRequiredContext(MerchantRoutesContext);
    const { conversation } = data;
    const conversationHref = getConversationHref(routes, conversation.topicId);
    const visibleMessages = conversation.messages.slice(-MESSAGE_PREVIEW_COUNT);
    const hasHiddenMessages =
      conversation.messages.length > MESSAGE_PREVIEW_COUNT;

    return (
      <MiniDetailSurface
        actionHref={conversationHref}
        actionLabel="View conversation"
        badge={<StateBadge conversation={conversation} />}
        subtitle={conversation.channel.toUpperCase()}
        title="Conversation preview"
      >
        <Flex dir="column" gap="sm">
          {visibleMessages.map((message) => (
            <MessageRow key={message.id} message={message} />
          ))}
        </Flex>

        {hasHiddenMessages && (
          <Text fontSize="xxs" textAlign="center" textColor="secondary">
            View conversation to see the rest.
          </Text>
        )}
      </MiniDetailSurface>
    );
  },
);

export const ConciergeCustomerCard = memo(function ConciergeCustomerCard({
  data,
}: {
  data: ConciergeCustomerOutput;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const id = customerId(data.customer);
  const name = customerDisplayName(data.customer);
  const email = primaryCustomerEmail(data.customer);
  const phone = primaryCustomerPhone(data.customer);
  const tags = data.customer.tags ?? [];
  const subscriptionStatuses = data.customer.marketingSubscriptionStatuses;
  const footerDetails =
    email || phone || data.customer.origin ? (
      <>
        {email && <FooterDetail label="Email" value={email} />}
        {phone && <FooterDetail label="Phone" value={phone} />}
        {data.customer.origin && (
          <FooterDetail label="Origin" value={data.customer.origin} />
        )}
      </>
    ) : undefined;

  return (
    <MiniDetailSurface
      actionHref={id ? routes.getCustomerRecordHref({ customerId: id }) : null}
      actionLabel="View customer"
      footerDetails={footerDetails}
      subtitle={[email, phone].filter(Boolean).join(" · ") || undefined}
      title={name}
    >
      <Flex gap="xs" wrap="wrap">
        {subscriptionStatuses?.email && (
          <RedoBadge
            color="gray"
            size="xs"
            text={`Email: ${subscriptionStatuses.email}`}
          />
        )}
        {subscriptionStatuses?.sms && (
          <RedoBadge
            color="gray"
            size="xs"
            text={`SMS: ${subscriptionStatuses.sms}`}
          />
        )}
        {tags.slice(0, 6).map((tag) => (
          <RedoBadge color="gray" key={tag} size="xs" text={tag} />
        ))}
        {tags.length > 6 && (
          <RedoBadge color="gray" size="xs" text={`+${tags.length - 6}`} />
        )}
        {!subscriptionStatuses?.email &&
          !subscriptionStatuses?.sms &&
          tags.length === 0 && (
            <Text fontSize="xs" textColor="secondary">
              No customer tags or marketing consent details returned.
            </Text>
          )}
      </Flex>
    </MiniDetailSurface>
  );
});

export const ConciergeKnowledgeCard = memo(function ConciergeKnowledgeCard({
  data,
}: {
  data: ConciergeKnowledge;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  const productCount = data.recommendedProductIds?.length ?? 0;

  return (
    <MiniDetailSurface
      actionHref={getKnowledgeHref(routes, data.id)}
      actionLabel="View knowledge"
      badge={
        <RedoBadge
          color={data.enabled ? "success" : "gray"}
          size="xs"
          text={data.enabled ? "Enabled" : "Disabled"}
        />
      }
      footerDetails={
        <>
          {productCount > 0 && (
            <FooterDetail
              label="Recommended products"
              value={String(productCount)}
            />
          )}
          {data.expiresAt && (
            <FooterDetail
              label="Expires"
              value={DATE_FORMAT.format(new Date(data.expiresAt))}
            />
          )}
        </>
      }
      subtitle={data.whenToUse || undefined}
      title={data.title}
    >
      <Text className={styles.previewText} fontSize="xs" textColor="primary">
        {data.knowledge}
      </Text>
    </MiniDetailSurface>
  );
});

export const ConciergeKnowledgeListCard = memo(
  function ConciergeKnowledgeListCard({
    data,
  }: {
    data: ConciergeKnowledgeSearchOutput;
  }) {
    const [isExpanded, setIsExpanded] = useState(false);
    const visibleKnowledge = isExpanded
      ? data
      : data.slice(0, KNOWLEDGE_PREVIEW_COUNT);
    const hiddenCount = Math.max(0, data.length - KNOWLEDGE_PREVIEW_COUNT);

    if (data.length === 1) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return <ConciergeKnowledgeCard data={data[0]!} />;
    }

    const title =
      data.length === 0
        ? "No Commerce Agent knowledge found"
        : `${data.length} Commerce Agent knowledge items`;

    return (
      <MiniDetailSurface
        footerDetails={<FooterDetail label="Showing" value={title} />}
        subtitle="Matching Commerce Agent knowledge"
        title="Commerce Agent knowledge"
      >
        {data.length === 0 ? (
          <Text fontSize="xs" textColor="secondary">
            No matching Commerce Agent knowledge found.
          </Text>
        ) : (
          <Flex dir="column" gap="sm">
            {visibleKnowledge.map((knowledge) => (
              <KnowledgeSummaryRow key={knowledge.id} knowledge={knowledge} />
            ))}
          </Flex>
        )}

        {hiddenCount > 0 && (
          <AskRedoDisclosureButton
            isOpen={isExpanded}
            onToggle={() => setIsExpanded((value) => !value)}
          >
            <Text fontSize="xs" fontWeight="semibold" textColor="brand-primary">
              {isExpanded ? "Show fewer" : `Show ${hiddenCount} more`}
            </Text>
          </AskRedoDisclosureButton>
        )}
      </MiniDetailSurface>
    );
  },
);

const AutomationSummaryRow = memo(function AutomationSummaryRow({
  automationHref,
  data,
  isLast,
}: {
  automationHref: string;
  data: ConciergeAutomationDetailOutput;
  isLast: boolean;
}) {
  const { automation, concierge } = data;
  const navigate = useNavigate();

  return (
    <MiniListRow
      hasDivider={!isLast}
      onClick={() => navigate(automationHref)}
      py="md"
    >
      <Flex align="flex-start" gap="md" justify="space-between">
        <Flex dir="column" gap="xs" minW="none">
          <Text fontSize="xs" fontWeight="semibold">
            {automation.name}
          </Text>
          <Text
            className={styles.previewText}
            fontSize="xs"
            textColor="secondary"
          >
            {automation.description ||
              `Automation with ${stepCountLabel(automation.stepCount)}`}
          </Text>
        </Flex>
        <AutomationEnabledBadge enabled={automation.enabled} />
      </Flex>
      <Flex gap="xs" wrap="wrap">
        <RedoBadge
          color="gray"
          size="xs"
          text={stepCountLabel(automation.stepCount)}
        />
        <RedoBadge
          color="gray"
          size="xs"
          text={`Starts from ${concierge.triggerName}`}
        />
      </Flex>
    </MiniListRow>
  );
});

const ConversationSummaryRow = memo(function ConversationSummaryRow({
  conversation,
  conversationHref,
  isLast,
}: {
  conversation: ConciergeConversationSummary;
  conversationHref: string;
  isLast: boolean;
}) {
  const navigate = useNavigate();
  const lastActivityAt =
    conversation.lastCustomerMessageAt ?? conversation.lastMerchantMessageAt;
  const lastMessage =
    conversation.lastCustomerMessageContent || "No customer messages yet.";
  const totalMessages =
    conversation.numCustomerMessages + conversation.numMerchantMessages;
  const customerLabel = conversationCustomerLabel(conversation);

  return (
    <MiniListRow
      hasDivider={!isLast}
      onClick={() => navigate(conversationHref)}
    >
      <Flex align="center" gap="sm" justify="space-between" w="full">
        <Text
          fontSize="sm"
          fontWeight="semibold"
          overflow="hidden"
          textColor="primary"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
        >
          {customerLabel}
        </Text>
        {lastActivityAt && (
          <Flex shrink={0}>
            <Text fontSize="xs" textColor="secondary">
              <SmartDateDisplay date={lastActivityAt} />
            </Text>
          </Flex>
        )}
      </Flex>
      <Text
        fontSize="xs"
        overflow="hidden"
        textColor="secondary"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {lastMessage}
      </Text>
      <Flex align="center" gap="xs" wrap="wrap">
        <StateBadge conversation={conversation} />
        <RedoBadge
          color="gray"
          size="xs"
          text={`${totalMessages} ${totalMessages === 1 ? "message" : "messages"}`}
        />
      </Flex>
    </MiniListRow>
  );
});

const ConfigChangeRow = memo(function ConfigChangeRow({
  change,
  hasDivider,
}: {
  change: ConciergeConfigUpdateOutput["changes"][number];
  hasDivider: boolean;
}) {
  const now = parseChangeValue(change.after);

  return (
    <MiniListRow hasDivider={hasDivider} py="md">
      <Flex dir="column" gap="xxs" minW="none">
        <Text fontSize="xs" fontWeight="semibold">
          {change.label}
        </Text>
        <Text fontSize="xs" textColor="secondary">
          {formatChangeSummary(change)}
        </Text>
      </Flex>
      {now.status && (
        <RedoBadge
          color={
            now.status === "On" || now.status === "Active" ? "success" : "gray"
          }
          size="xs"
          text={now.status}
        />
      )}
    </MiniListRow>
  );
});

const KnowledgeSummaryRow = memo(function KnowledgeSummaryRow({
  knowledge,
}: {
  knowledge: ConciergeKnowledge;
}) {
  const routes = useRequiredContext(MerchantRoutesContext);
  return (
    <InlineDetailSurface
      actionHref={getKnowledgeHref(routes, knowledge.id)}
      actionLabel="Open"
      badge={
        <RedoBadge
          color={knowledge.enabled ? "success" : "gray"}
          size="xs"
          text={knowledge.enabled ? "Enabled" : "Disabled"}
        />
      }
      footerDetails={
        <>
          {knowledge.recommendedProductIds &&
            knowledge.recommendedProductIds.length > 0 && (
              <MetaText
                label="Recommended products"
                value={String(knowledge.recommendedProductIds.length)}
              />
            )}
          {knowledge.expiresAt && (
            <MetaText
              label="Expires"
              value={DATE_FORMAT.format(new Date(knowledge.expiresAt))}
            />
          )}
        </>
      }
      title={knowledge.title}
    >
      <Text className={styles.previewText} fontSize="xs" textColor="secondary">
        {knowledge.knowledge}
      </Text>
    </InlineDetailSurface>
  );
});

const MessageRow = memo(function MessageRow({
  message,
}: {
  message: ConciergeMessage;
}) {
  const direction = messageDirection(message);

  if (direction === "system") {
    return (
      <Flex align="center" dir="column" gap="xs" px="xl" w="full">
        <Flex align="center" gap="sm" w="full">
          <Flex
            borderBottomWidth="1px"
            borderColor="secondary"
            borderStyle="solid"
            grow={1}
          />
          <Flex
            align="center"
            bgColor="primary"
            borderColor="secondary"
            borderRadius="md"
            borderStyle="solid"
            borderWidth="1px"
            gap="xs"
            px="md"
            py="xs"
          >
            <Text fontSize="xs" fontWeight="semibold" textColor="secondary">
              System
            </Text>
            <Text fontSize="xs" textColor="secondary">
              {DATE_FORMAT.format(message.sentAt)}
            </Text>
          </Flex>
          <Flex
            borderBottomWidth="1px"
            borderColor="secondary"
            borderStyle="solid"
            grow={1}
          />
        </Flex>
        <Text fontSize="xs" textAlign="center" textColor="secondary">
          {message.content || "No message content"}
        </Text>
      </Flex>
    );
  }

  const isOutgoing = direction === "outgoing";

  return (
    <Flex
      dir="column"
      gap="xs"
      pl={isOutgoing ? "5xl" : "none"}
      pr={isOutgoing ? "none" : "5xl"}
      w="full"
    >
      <Flex
        align={isOutgoing ? "flex-end" : "flex-start"}
        dir="column"
        gap="xs"
      >
        <Text fontSize="xs" textColor="secondary">
          {messageSenderLabel(message.senderType)} ·{" "}
          {DATE_FORMAT.format(message.sentAt)}
        </Text>
        <Flex
          bgColor={isOutgoing ? "secondary" : "tertiary"}
          borderColor={isOutgoing ? "secondary" : "transparent"}
          borderRadius="lg"
          borderStyle="solid"
          borderWidth="1px"
          className={styles.messageText}
          p="lg"
          w="fit-content"
        >
          <Text fontSize="xs" textColor="primary">
            {message.content || "No message content"}
          </Text>
        </Flex>
      </Flex>
    </Flex>
  );
});

export const MiniDetailSurface = memo(function MiniDetailSurface({
  title,
  subtitle,
  badge,
  children,
  footerDetails,
  actionHref,
  actionLabel,
}: {
  title: string;
  subtitle?: string;
  badge?: ReactNode;
  children: ReactNode;
  footerDetails?: ReactNode;
  actionHref?: string | null;
  actionLabel?: string;
}) {
  const hasActionFooter = Boolean(actionHref && actionLabel);
  return (
    <Flex
      bgColor="primary"
      borderColor="secondary"
      borderRadius="md"
      borderStyle="solid"
      borderWidth="1px"
      dir="column"
      gap="none"
    >
      <Flex align="flex-start" gap="md" justify="space-between" p="lg">
        <Flex dir="column" gap="xxs" minW="none">
          <Text fontSize="sm" fontWeight="semibold">
            {title}
          </Text>
          {subtitle && (
            <Text fontSize="xs" textColor="secondary">
              {subtitle}
            </Text>
          )}
        </Flex>
        {badge}
      </Flex>
      <Flex dir="column" gap="md" px="lg" py={hasActionFooter ? "none" : "lg"}>
        {children}
        {footerDetails && (
          <Flex align="center" gap="lg" minW="none" wrap="wrap">
            {footerDetails}
          </Flex>
        )}
      </Flex>
      {hasActionFooter && (
        <Flex
          align="center"
          borderColor="secondary"
          borderStyle="solid"
          borderTopWidth="1px"
          justify="flex-end"
          mt="lg"
          p="md"
        >
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
          <ActionButton href={actionHref!} label={actionLabel!} />
        </Flex>
      )}
    </Flex>
  );
});

const InlineDetailSurface = memo(function InlineDetailSurface({
  title,
  badge,
  children,
  footerDetails,
  actionHref,
  actionLabel,
}: {
  title: string;
  badge?: ReactNode;
  children: ReactNode;
  footerDetails?: ReactNode;
  actionHref: string;
  actionLabel: string;
}) {
  return (
    <Flex bgColor="secondary" borderRadius="md" dir="column" gap="none">
      <Flex align="flex-start" gap="md" justify="space-between" p="md">
        <Flex dir="column" gap="xs" minW="none">
          <Text fontSize="xs" fontWeight="semibold">
            {title}
          </Text>
          {children}
          {footerDetails && (
            <Flex gap="md" wrap="wrap">
              {footerDetails}
            </Flex>
          )}
        </Flex>
        {badge}
      </Flex>
      <Flex
        borderColor="secondary"
        borderStyle="solid"
        borderTopWidth="1px"
        justify="flex-end"
        p="sm"
      >
        <ActionButton href={actionHref} label={actionLabel} />
      </Flex>
    </Flex>
  );
});

const ConfigSection = memo(function ConfigSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <Flex dir="column" gap="sm">
      <Text fontSize="xs" fontWeight="semibold">
        {title}
      </Text>
      <FullBleedList>{children}</FullBleedList>
    </Flex>
  );
});

const FullBleedList = memo(function FullBleedList({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <Flex className={styles.fullBleedList} dir="column" gap="none">
      {children}
    </Flex>
  );
});

const ConfigSnapshotRow = memo(function ConfigSnapshotRow({
  label,
  value,
  badge,
  borderBottom = true,
}: {
  label: string;
  value: string;
  badge?: ReactNode;
  borderBottom?: boolean;
}) {
  return (
    <MiniListRow hasDivider={borderBottom} py="sm">
      <Flex dir="column" gap="xxs" minW="none">
        <Text fontSize="xs" fontWeight="semibold">
          {label}
        </Text>
        <Text fontSize="xs" textColor="secondary">
          {value}
        </Text>
      </Flex>
      {badge && <Flex shrink={0}>{badge}</Flex>}
    </MiniListRow>
  );
});

const StatusBadge = memo(function StatusBadge({
  enabled,
  enabledText,
  offText,
}: {
  enabled: boolean | undefined;
  enabledText: string;
  offText: string;
}) {
  return (
    <RedoBadge
      color={enabled ? "success" : "gray"}
      size="xs"
      text={enabled ? enabledText : offText}
    />
  );
});

const FooterDetail = memo(function FooterDetail({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <Flex dir="column" gap="none" minW="none">
      <Text fontSize="xs" textColor="secondary">
        {label}
      </Text>
      <Text fontSize="xs" fontWeight="medium" textColor="secondary">
        {value}
      </Text>
    </Flex>
  );
});

const ActionButton = memo(function ActionButton({
  href,
  label,
}: {
  href: string;
  label: string;
}) {
  const navigate = useNavigate();
  return (
    <RedoButton
      hierarchy="secondary"
      onClick={() => navigate(href)}
      size="xs"
      text={label}
    />
  );
});

const MetaText = memo(function MetaText({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <Text fontSize="xs" textColor="secondary">
      {label}: {value}
    </Text>
  );
});

const AutomationEnabledBadge = memo(function AutomationEnabledBadge({
  enabled,
}: {
  enabled: boolean;
}) {
  return (
    <RedoBadge
      color={enabled ? "success" : "gray"}
      size="xs"
      text={enabled ? "Enabled" : "Disabled"}
    />
  );
});

const StateBadge = memo(function StateBadge({
  conversation,
}: {
  conversation: ConciergeConversationSummary;
}) {
  const state = conversationState(conversation);
  const color =
    state === "Escalated" ? "error" : state === "Replied" ? "success" : "gray";
  return <RedoBadge color={color} size="xs" text={state} />;
});

function stepCountLabel(
  stepCount: number | undefined,
  steps?: ConciergeAutomationDetailOutput["automation"]["steps"],
): string {
  const count = stepCount ?? steps?.length ?? 0;
  return `${count} ${count === 1 ? "step" : "steps"}`;
}

function conversationCustomerLabel(
  conversation: ConciergeConversationSummary,
): string {
  const customer = conversation.customer;
  const fullName = [customer?.firstName, customer?.lastName]
    .filter(Boolean)
    .join(" ");

  return (
    customer?.displayName ||
    fullName ||
    customer?.email ||
    customer?.phone ||
    `Customer ${shortId(conversation.mongoCustomerId)}`
  );
}

function formatChangeSummary({
  before,
  after,
}: ConciergeConfigUpdateOutput["changes"][number]): string {
  const beforeValue = parseChangeValue(before);
  const afterValue = parseChangeValue(after);
  if (
    beforeValue.label &&
    afterValue.label &&
    beforeValue.label === afterValue.label
  ) {
    return `${beforeValue.label}: ${beforeValue.value} to ${afterValue.value}`;
  }
  return `${beforeValue.display} to ${afterValue.display}`;
}

function parseChangeValue(value: string): {
  display: string;
  label: string | null;
  status: string | null;
  value: string;
} {
  const trimmed = value.trim();
  const labeled = /^([A-Z][A-Z_ ]*): (.+)$/.exec(trimmed);
  if (!labeled) {
    return {
      display: trimmed,
      label: null,
      status: simpleStatus(trimmed),
      value: trimmed,
    };
  }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const label = titleCase(labeled[1]!);
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const status = labeled[2]!.trim();
  return {
    display: `${label}: ${status}`,
    label,
    status: simpleStatus(status),
    value: status,
  };
}

function simpleStatus(value: string): string | null {
  return ["On", "Off", "Active", "Inactive"].includes(value) ? value : null;
}

function titleCase(value: string): string {
  return value
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function messageDirection(
  message: ConciergeMessage,
): "incoming" | "outgoing" | "system" {
  if (message.senderType === "system" || message.channel === "internalNote") {
    return "system";
  }
  switch (message.senderType) {
    case "merchant":
    case "ai":
    case "auto_reply":
    case "chat_flow":
      return "outgoing";
    default:
      return "incoming";
  }
}

function messageSenderLabel(senderType: string): string {
  switch (senderType) {
    case "customer":
      return "Customer";
    case "ai":
      return "Commerce Agent";
    case "merchant":
      return "Merchant";
    case "auto_reply":
      return "Auto-reply";
    case "chat_flow":
      return "Chat flow";
    default:
      return senderType;
  }
}

function shortId(id: string): string {
  return id.length > 8 ? `${id.slice(0, 4)}...${id.slice(-4)}` : id;
}

function getConversationHref(routes: MerchantRoutes, topicId: string): string {
  return `${routes.getShopperAgentSubRoute("conversations")}?selected=${encodeURIComponent(
    topicId,
  )}`;
}

function getKnowledgeHref(
  routes: MerchantRoutes,
  knowledgeId:
    | MerchantKnowledgeId
    | FaqKnowledgeId
    | SuggestedMerchantKnowledgeId,
): string {
  // The knowledge page resolves the corpus from the id's own prefix, so an
  // article, an FAQ entry, and a proposal all deep-link the same way.
  return `${routes.getShopperAgentSubRoute("knowledge")}?knowledge=${encodeURIComponent(knowledgeId)}`;
}

function getAutomationHref(
  routes: MerchantRoutes,
  automationId: string,
): string {
  return `${routes.automationsHref}/${encodeURIComponent(automationId)}`;
}

function isChannelEnabled(
  data: ConciergeConfigOutput,
  channel: string,
): boolean {
  const config = data.settings?.channels?.[channel];
  if (config && typeof config === "object" && "enabled" in config) {
    const enabled = (config as { enabled: unknown }).enabled;
    if (typeof enabled === "boolean") {
      return enabled;
    }
  }
  const definition = data.channelDefinitions?.find(
    (entry) => entry.channel === channel,
  );
  return definition?.defaultEnabled ?? false;
}

function triggerName(trigger: ConciergeConfigOutput["trigger"]): string {
  return trigger?.definition?.name ?? trigger?.id ?? "Trigger";
}

function triggerSubtitle(trigger: ConciergeConfigOutput["trigger"]): string {
  if (!trigger) {
    return "";
  }
  if (!trigger.supported) {
    return trigger.unsupportedReason ?? "Not supported by Commerce Agent";
  }
  return "";
}
